import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import '../AuthWarningModal.css';
import './Rules.css';

const MERCHANT_NAME_MAX_LEN = 120;

function generateLocalId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

// Predefined color palette for categories
const CATEGORY_COLORS = [
  { bg: '#dbeafe', text: '#1d4ed8' }, // Blue (NSK)
  { bg: '#f3e8ff', text: '#7c3aed' }, // Purple (OTHER)
  { bg: '#dcfce7', text: '#166534' }, // Green
  { bg: '#fef3c7', text: '#92400e' }, // Yellow
  { bg: '#fce7f3', text: '#be185d' }, // Pink
  { bg: '#e0e7ff', text: '#4338ca' }, // Indigo
  { bg: '#fef2f2', text: '#991b1b' }, // Red
  { bg: '#ecfdf5', text: '#065f46' }, // Emerald
  { bg: '#fff7ed', text: '#9a3412' }, // Orange
  { bg: '#f0f9ff', text: '#0c4a6e' }, // Sky
  { bg: '#f5f3ff', text: '#5b21b6' }, // Violet
  { bg: '#fefce8', text: '#713f12' }, // Lime
];

// Generate a stable color for a category based on its name
const getCategoryColor = (categoryName) => {
  // Use predefined colors for known categories
  const lowerName = categoryName.toLowerCase();
  if (lowerName === 'nsk') {
    return CATEGORY_COLORS[0];
  }
  if (lowerName === 'other') {
    return CATEGORY_COLORS[1];
  }
  
  // For other categories, use a hash function to get a stable color
  let hash = 0;
  for (let i = 0; i < categoryName.length; i++) {
    hash = categoryName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colorIndex = Math.abs(hash) % CATEGORY_COLORS.length;
  return CATEGORY_COLORS[colorIndex];
};

const ReceiptScopeTab = forwardRef(function ReceiptScopeTab({ config, onUpdate, hideWizardSaveBar = false }, ref) {
  const [draftMerchants, setDraftMerchants] = useState(() => [...(config?.eligibleTenants || [])]);
  const [draftCategories, setDraftCategories] = useState(() => {
    const c = config?.tenantCategories;
    return c && c.length > 0 ? [...c] : [];
  });
  const [draftAmountThresholds, setDraftAmountThresholds] = useState(() => ({ ...(config?.amountThresholds || {}) }));
  const [draftReceiptQuantityRules, setDraftReceiptQuantityRules] = useState(() => {
    const rq = config?.receiptQuantityRules;
    return {
      maxTotalReceipts: rq?.maxTotalReceipts ?? 3,
      categoryLimits: { ...(rq?.categoryLimits || {}) }
    };
  });

  useEffect(() => {
    setDraftMerchants([...(config?.eligibleTenants || [])]);
  }, [JSON.stringify(config?.eligibleTenants || [])]);

  useEffect(() => {
    const c = config?.tenantCategories;
    setDraftCategories(c && c.length > 0 ? [...c] : []);
  }, [JSON.stringify(config?.tenantCategories || [])]);

  useEffect(() => {
    setDraftAmountThresholds({ ...(config?.amountThresholds || {}) });
  }, [JSON.stringify(config?.amountThresholds || {})]);

  useEffect(() => {
    const rq = config?.receiptQuantityRules;
    setDraftReceiptQuantityRules({
      maxTotalReceipts: rq?.maxTotalReceipts ?? 3,
      categoryLimits: { ...(rq?.categoryLimits || {}) }
    });
  }, [JSON.stringify(config?.receiptQuantityRules || [])]);

  const defaultCategory = draftCategories[0] || '';

  const [newMerchant, setNewMerchant] = useState({ name: '', category: defaultCategory });
  const [isAdding, setIsAdding] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [editingMerchantId, setEditingMerchantId] = useState(null);
  const [editingMerchantData, setEditingMerchantData] = useState({ name: '', category: '' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Modal states
  const [showDeleteMerchantModal, setShowDeleteMerchantModal] = useState(false);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalContent, setMessageModalContent] = useState({ title: '', message: '' });
  const [pendingDeleteMerchantId, setPendingDeleteMerchantId] = useState(null);
  const [pendingDeleteCategory, setPendingDeleteCategory] = useState(null);

  /** Commit add-merchant row to local draft only (persist with Save Changes). */
  const commitNewMerchantRow = () => {
    const trimmed = newMerchant.name.trim();
    if (!trimmed) return;
    if (trimmed.length > MERCHANT_NAME_MAX_LEN) {
      setMessageModalContent({
        title: 'Error',
        message: `Merchant name must be at most ${MERCHANT_NAME_MAX_LEN} characters.`,
      });
      setShowMessageModal(true);
      return;
    }
    if (draftCategories.length === 0) {
      setMessageModalContent({
        title: 'Category Required',
        message: 'Please add at least one merchant category before adding a merchant.'
      });
      setShowMessageModal(true);
      return;
    }
    const categoryToSend = draftCategories.includes(newMerchant.category) ? newMerchant.category : defaultCategory;
    setDraftMerchants((prev) => [
      ...prev,
      {
        id: generateLocalId(),
        name: trimmed,
        category: categoryToSend,
        createdAt: new Date().toISOString()
      }
    ]);
    setNewMerchant({ name: '', category: defaultCategory });
    setIsAdding(false);
  };

  const openAddMerchant = () => {
    if (draftCategories.length === 0) {
      setMessageModalContent({
        title: 'Category Required',
        message: 'Please add at least one merchant category before adding a merchant.'
      });
      setShowMessageModal(true);
      return;
    }
    setNewMerchant({ name: '', category: defaultCategory });
    setIsAdding(true);
  };

  const handleDeleteMerchant = (id) => {
    setPendingDeleteMerchantId(id);
    setShowDeleteMerchantModal(true);
  };

  const confirmDeleteMerchant = () => {
    if (pendingDeleteMerchantId == null) return;
    const id = pendingDeleteMerchantId;
    const removed = draftMerchants.find((t) => t.id === id);
    const deletedCategory = removed?.category;
    const nextMerchants = draftMerchants.filter((t) => t.id !== id);
    let nextCategories = [...draftCategories];
    let nextThresholds = { ...draftAmountThresholds };
    let nextRqr = {
      ...draftReceiptQuantityRules,
      categoryLimits: { ...draftReceiptQuantityRules.categoryLimits }
    };
    if (deletedCategory) {
      const stillUsed = nextMerchants.some((t) => t.category === deletedCategory);
      if (!stillUsed) {
        nextCategories = nextCategories.filter((c) => c !== deletedCategory);
        delete nextThresholds[deletedCategory];
        if (nextRqr.categoryLimits && deletedCategory in nextRqr.categoryLimits) {
          delete nextRqr.categoryLimits[deletedCategory];
        }
      }
    }
    setDraftMerchants(nextMerchants);
    setDraftCategories(nextCategories);
    setDraftAmountThresholds(nextThresholds);
    setDraftReceiptQuantityRules(nextRqr);
    setShowDeleteMerchantModal(false);
    setPendingDeleteMerchantId(null);
  };

  // Start editing a merchant
  const startEditMerchant = (merchant) => {
    setEditingMerchantId(merchant.id);
    setEditingMerchantData({ name: merchant.name, category: merchant.category });
  };

  /** Commit edit row to local draft only (persist with Save Changes). */
  const commitEditMerchantRow = () => {
    const trimmed = editingMerchantData.name.trim();
    if (!trimmed) return;
    if (trimmed.length > MERCHANT_NAME_MAX_LEN) {
      setMessageModalContent({
        title: 'Error',
        message: `Merchant name must be at most ${MERCHANT_NAME_MAX_LEN} characters.`,
      });
      setShowMessageModal(true);
      return;
    }
    setDraftMerchants((prev) =>
      prev.map((t) =>
        t.id === editingMerchantId ? { ...t, name: trimmed, category: editingMerchantData.category } : t
      )
    );
    setEditingMerchantId(null);
    setEditingMerchantData({ name: '', category: '' });
  };

  // Cancel editing
  const cancelEditMerchant = () => {
    setEditingMerchantId(null);
    setEditingMerchantData({ name: '', category: '' });
  };

  /** Add category to local draft only (persist with Save Changes). */
  const handleAddCategory = () => {
    const categoryName = newCategory.trim().toUpperCase();
    if (!categoryName) return;
    if (draftCategories.includes(categoryName)) {
      setMessageModalContent({ title: 'Error', message: 'This category already exists' });
      setShowMessageModal(true);
      return;
    }
    setDraftCategories((prev) => [...prev, categoryName]);
    setDraftAmountThresholds((prev) => ({ ...prev, [categoryName]: 0 }));
    setDraftReceiptQuantityRules((prev) => ({
      ...prev,
      categoryLimits: { ...(prev.categoryLimits || {}), [categoryName]: 1 }
    }));
    setNewCategory('');
    setIsAddingCategory(false);
  };

  // Delete category
  const handleDeleteCategory = (categoryToDelete) => {
    // Check if any merchant is using this category
    const merchantsUsingCategory = draftMerchants.filter(t => t.category === categoryToDelete);
    if (merchantsUsingCategory.length > 0) {
      setMessageModalContent({ 
        title: 'Error', 
        message: `Cannot delete "${categoryToDelete}" - ${merchantsUsingCategory.length} merchant(s) are using this category. Please reassign them first.` 
      });
      setShowMessageModal(true);
      return;
    }
    
    setPendingDeleteCategory(categoryToDelete);
    setShowDeleteCategoryModal(true);
  };

  const confirmDeleteCategory = () => {
    if (!pendingDeleteCategory) return;
    const categoryToDelete = pendingDeleteCategory;
    setShowDeleteCategoryModal(false);
    setDraftCategories((prev) => prev.filter((c) => c !== categoryToDelete));
    setDraftAmountThresholds((prev) => {
      const next = { ...prev };
      delete next[categoryToDelete];
      return next;
    });
    setDraftReceiptQuantityRules((prev) => {
      const limits = { ...(prev.categoryLimits || {}) };
      delete limits[categoryToDelete];
      return { ...prev, categoryLimits: limits };
    });
    setPendingDeleteCategory(null);
  };

  const persistReceiptScopeStep = useCallback(async () => {
    let merchantsToSave = [...draftMerchants];
    let categoriesToSave = [...draftCategories];
    let thresholdsToSave = { ...draftAmountThresholds };
    let rqrToSave = {
      ...draftReceiptQuantityRules,
      categoryLimits: { ...draftReceiptQuantityRules.categoryLimits }
    };

    if (editingMerchantId) {
      const trimmed = editingMerchantData.name.trim();
      if (!trimmed) {
        return { ok: false, error: 'Finish editing the merchant name or cancel before saving.' };
      }
      if (trimmed.length > MERCHANT_NAME_MAX_LEN) {
        return { ok: false, error: `Merchant name must be at most ${MERCHANT_NAME_MAX_LEN} characters.` };
      }
      merchantsToSave = merchantsToSave.map((t) =>
        t.id === editingMerchantId ? { ...t, name: trimmed, category: editingMerchantData.category } : t
      );
    }

    if (isAdding) {
      const trimmed = newMerchant.name.trim();
      if (trimmed) {
        if (trimmed.length > MERCHANT_NAME_MAX_LEN) {
          return { ok: false, error: `Merchant name must be at most ${MERCHANT_NAME_MAX_LEN} characters.` };
        }
        if (categoriesToSave.length === 0) {
          return { ok: false, error: 'Please add at least one merchant category before adding a merchant.' };
        }
        const categoryToSend = categoriesToSave.includes(newMerchant.category) ? newMerchant.category : defaultCategory;
        merchantsToSave = [
          ...merchantsToSave,
          {
            id: generateLocalId(),
            name: trimmed,
            category: categoryToSend,
            createdAt: new Date().toISOString()
          }
        ];
      }
    }

    if (isAddingCategory) {
      const categoryName = newCategory.trim().toUpperCase();
      if (categoryName) {
        if (categoriesToSave.includes(categoryName)) {
          return { ok: false, error: 'This category already exists.' };
        }
        categoriesToSave = [...categoriesToSave, categoryName];
        thresholdsToSave = { ...thresholdsToSave, [categoryName]: 0 };
        rqrToSave = {
          ...rqrToSave,
          categoryLimits: { ...rqrToSave.categoryLimits, [categoryName]: 1 }
        };
      }
    }

    setSaving(true);
    try {
      await onUpdate({
        eligibleTenants: merchantsToSave,
        tenantCategories: categoriesToSave,
        amountThresholds: thresholdsToSave,
        receiptQuantityRules: rqrToSave
      });
      setEditingMerchantId(null);
      setEditingMerchantData({ name: '', category: '' });
      setIsAdding(false);
      setNewMerchant({ name: '', category: defaultCategory });
      setIsAddingCategory(false);
      setNewCategory('');
      return { ok: true };
    } catch {
      return { ok: false, error: 'Failed to save receipt scope.' };
    } finally {
      setSaving(false);
    }
  }, [
    draftMerchants,
    draftCategories,
    draftAmountThresholds,
    draftReceiptQuantityRules,
    defaultCategory,
    editingMerchantId,
    editingMerchantData,
    isAdding,
    newMerchant,
    isAddingCategory,
    newCategory,
    onUpdate
  ]);

  useImperativeHandle(ref, () => ({
    saveStep: persistReceiptScopeStep
  }), [persistReceiptScopeStep]);

  const handleSaveWizardStep = async () => {
    setMessage(null);
    const result = await persistReceiptScopeStep();
    if (result && result.ok === false) {
      setMessage({ type: 'error', text: result.error });
      return;
    }
    setMessage({ type: 'success', text: 'Receipt scope saved successfully!' });
    setTimeout(() => setMessage(null), 3000);
  };

  const isDirty = useMemo(() => {
    const baseMerchants = config?.eligibleTenants || [];
    const baseCats = config?.tenantCategories?.length ? [...config.tenantCategories] : [];
    const baseThr = config?.amountThresholds || {};
    const baseRq = {
      maxTotalReceipts: config?.receiptQuantityRules?.maxTotalReceipts ?? 3,
      categoryLimits: { ...(config?.receiptQuantityRules?.categoryLimits || {}) }
    };
    const structuralDirty =
      JSON.stringify(draftMerchants) !== JSON.stringify(baseMerchants) ||
      JSON.stringify(draftCategories) !== JSON.stringify(baseCats) ||
      JSON.stringify(draftAmountThresholds) !== JSON.stringify(baseThr) ||
      JSON.stringify(draftReceiptQuantityRules) !== JSON.stringify(baseRq);
    if (isAdding && newMerchant.name.trim()) return true;
    if (isAddingCategory && newCategory.trim()) return true;
    if (editingMerchantId) {
      const cur = draftMerchants.find((m) => m.id === editingMerchantId);
      if (!cur) return true;
      if (cur.name !== editingMerchantData.name.trim() || cur.category !== editingMerchantData.category) return true;
    }
    return structuralDirty;
  }, [
    config?.eligibleTenants,
    config?.tenantCategories,
    config?.amountThresholds,
    config?.receiptQuantityRules,
    draftMerchants,
    draftCategories,
    draftAmountThresholds,
    draftReceiptQuantityRules,
    editingMerchantId,
    editingMerchantData.name,
    editingMerchantData.category,
    isAdding,
    newMerchant.name,
    isAddingCategory,
    newCategory
  ]);

  return (
    <div className="tab-panel">
      <div className="tab-header">
        <h2>Receipt Scope Rules</h2>
        <p>Define which merchants/stores are eligible for claims. Only receipts from these merchants will be accepted.</p>
      </div>

      <div className="rules-form">
        <div className="form-section">
          <div className="form-section-title">Eligible Merchants</div>
          
          <div className="items-list">
            {draftMerchants.length === 0 ? (
              <div className="empty-state-mini">
                <p>No merchants configured. Add merchants to define eligible stores.</p>
              </div>
            ) : (
              draftMerchants.map(merchant => (
                <div key={merchant.id} className="item-card">
                  {editingMerchantId === merchant.id ? (
                    // Edit mode
                    <>
                      <div className="item-info item-info--scope" style={{ flex: 1, minWidth: 0 }}>
                        <div className="merchant-name-field rules-field-group">
                          <label className="rules-field-label" htmlFor={`edit-merchant-name-${editingMerchantId}`}>
                            Merchant name
                          </label>
                          <input
                            id={`edit-merchant-name-${editingMerchantId}`}
                            type="text"
                            value={editingMerchantData.name}
                            onChange={(e) => setEditingMerchantData({ ...editingMerchantData, name: e.target.value })}
                            maxLength={MERCHANT_NAME_MAX_LEN}
                            placeholder={`e.g., NSK Trade City — max ${MERCHANT_NAME_MAX_LEN} characters`}
                            style={{ 
                              width: '100%',
                              boxSizing: 'border-box',
                              padding: '0.5rem 0.75rem',
                              border: '1.5px solid #3b82f6',
                              borderRadius: '6px',
                              fontSize: '0.9375rem'
                            }}
                            autoFocus
                          />
                        </div>
                        <div className="rules-field-group rules-field-group--category">
                          <label className="rules-field-label" htmlFor={`edit-merchant-category-${editingMerchantId}`}>
                            Category
                          </label>
                          <select
                            id={`edit-merchant-category-${editingMerchantId}`}
                            className="rules-select"
                            value={editingMerchantData.category}
                            onChange={(e) => setEditingMerchantData({ ...editingMerchantData, category: e.target.value })}
                            style={{
                              border: '1.5px solid #3b82f6',
                              borderRadius: '6px',
                              fontSize: '0.9375rem',
                            }}
                          >
                            {draftCategories.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="item-actions item-actions--scope">
                        <button 
                          className="btn-icon-sm"
                          onClick={commitEditMerchantRow}
                          title="Confirm row"
                          style={{ background: '#dcfce7', color: '#166534' }}
                        >
                          ✓
                        </button>
                        <button 
                          className="btn-icon-sm"
                          onClick={cancelEditMerchant}
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    </>
                  ) : (
                    // View mode
                    <>
                      <div className="item-info">
                        <div className="item-details">
                          <h4>{merchant.name}</h4>
                          <span 
                            className="category-badge"
                            style={{
                              background: getCategoryColor(merchant.category).bg,
                              color: getCategoryColor(merchant.category).text
                            }}
                          >
                            {merchant.category}
                          </span>
                        </div>
                      </div>
                      <div className="item-actions">
                        <button 
                          className="btn-icon-sm"
                          onClick={() => startEditMerchant(merchant)}
                          title="Edit merchant"
                          style={{ background: '#e0f2fe', color: '#0369a1' }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                        <button 
                          className="btn-icon-sm delete"
                          onClick={() => handleDeleteMerchant(merchant.id)}
                          title="Remove merchant"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          </svg>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}

            {isAdding ? (
              <div className="item-card" style={{ background: '#f8fafc' }}>
                <div className="item-info item-info--scope" style={{ flex: 1, minWidth: 0 }}>
                  <div className="merchant-name-field rules-field-group">
                    <label className="rules-field-label" htmlFor="new-merchant-name">
                      Merchant name
                    </label>
                    <input
                      id="new-merchant-name"
                      type="text"
                      placeholder={`e.g., NSK Trade City — max ${MERCHANT_NAME_MAX_LEN} characters`}
                      value={newMerchant.name}
                      onChange={(e) => setNewMerchant({ ...newMerchant, name: e.target.value })}
                      maxLength={MERCHANT_NAME_MAX_LEN}
                      style={{ 
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '0.5rem 0.75rem',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '0.9375rem'
                      }}
                      autoFocus
                    />
                  </div>
                  <div className="rules-field-group rules-field-group--category">
                    <label className="rules-field-label" htmlFor="new-merchant-category">
                      Category
                    </label>
                    <select
                      id="new-merchant-category"
                      className="rules-select"
                      value={draftCategories.includes(newMerchant.category) ? newMerchant.category : defaultCategory}
                      onChange={(e) => setNewMerchant({ ...newMerchant, category: e.target.value })}
                      disabled={draftCategories.length === 0}
                      style={{
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '0.9375rem',
                      }}
                    >
                      {draftCategories.length === 0 && (
                        <option value="">No categories yet</option>
                      )}
                      {draftCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="item-actions item-actions--scope">
                  <button 
                    className="btn-icon-sm"
                    onClick={commitNewMerchantRow}
                    title="Confirm row"
                    style={{ background: '#dcfce7', color: '#166534' }}
                  >
                    ✓
                  </button>
                  <button 
                    className="btn-icon-sm"
                    onClick={() => {
                      setIsAdding(false);
                      setNewMerchant({ name: '', category: defaultCategory });
                    }}
                    title="Cancel"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <button className="add-item-btn" onClick={openAddMerchant}>
                <span>+</span> Add Merchant
              </button>
            )}
          </div>
        </div>

        <div className="form-section">
          <div className="form-section-title">Merchant Categories</div>
          <p style={{ color: '#64748b', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
            Categories are used to group merchants and apply different rules per category.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {draftCategories.map(cat => {
              const color = getCategoryColor(cat);
              return (
                <div 
                  key={cat} 
                  className="category-badge"
                  style={{ 
                    fontSize: '0.875rem', 
                    padding: '0.375rem 0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    background: color.bg,
                    color: color.text
                  }}
                >
                  {cat}
                  <button
                    onClick={() => handleDeleteCategory(cat)}
                    disabled={saving}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0',
                      fontSize: '0.75rem',
                      opacity: 0.7,
                      marginLeft: '0.25rem',
                      color: color.text
                    }}
                    title={`Delete ${cat} category`}
                  >
                    ×
                  </button>
                </div>
              );
            })}
            
            {isAddingCategory ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="text"
                  placeholder="Category name"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddCategory();
                    if (e.key === 'Escape') {
                      setIsAddingCategory(false);
                      setNewCategory('');
                    }
                  }}
                  style={{
                    padding: '0.375rem 0.75rem',
                    border: '1.5px solid #3b82f6',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    width: '120px',
                    textTransform: 'uppercase'
                  }}
                  autoFocus
                />
                <button
                  onClick={handleAddCategory}
                  disabled={!newCategory.trim()}
                  style={{
                    background: '#dcfce7',
                    color: '#166534',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.375rem 0.75rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setIsAddingCategory(false);
                    setNewCategory('');
                  }}
                  style={{
                    background: '#f1f5f9',
                    color: '#64748b',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.375rem 0.75rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsAddingCategory(true)}
                style={{
                  background: '#f1f5f9',
                  color: '#475569',
                  border: '1px dashed #cbd5e1',
                  borderRadius: '20px',
                  padding: '0.375rem 0.875rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                + Add Category
              </button>
            )}
          </div>
        </div>

        {message && (
          <div className={`status-message ${message.type}`}>
            {message.type === 'success' ? '✓' : '✕'} {message.text}
          </div>
        )}

        {!hideWizardSaveBar && (
          <div className="save-bar">
            <button
              type="button"
              className="btn-save"
              onClick={handleSaveWizardStep}
              disabled={saving || !isDirty}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {/* Delete Merchant Confirmation Modal */}
      {showDeleteMerchantModal && (
        <div className="auth-modal-overlay" onClick={() => { setShowDeleteMerchantModal(false); setPendingDeleteMerchantId(null); }}>
          <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="auth-modal-title">Remove Merchant</h2>
            <p className="auth-modal-message">Are you sure you want to remove this merchant?</p>
            <div style={{ display: 'flex', gap: '0.75rem', width: '100%', marginTop: '1rem' }}>
              <button 
                className="auth-modal-button"
                onClick={() => { setShowDeleteMerchantModal(false); setPendingDeleteMerchantId(null); }}
                style={{ 
                  background: 'white', 
                  color: '#2d3748', 
                  border: '1px solid #cbd5e0',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Cancel
              </button>
              <button 
                className="auth-modal-button"
                onClick={confirmDeleteMerchant}
                style={{ 
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Category Confirmation Modal */}
      {showDeleteCategoryModal && pendingDeleteCategory && (
        <div className="auth-modal-overlay" onClick={() => { setShowDeleteCategoryModal(false); setPendingDeleteCategory(null); }}>
          <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="auth-modal-title">Delete Category</h2>
            <p className="auth-modal-message">Are you sure you want to delete the category "{pendingDeleteCategory}"?</p>
            <div style={{ display: 'flex', gap: '0.75rem', width: '100%', marginTop: '1rem' }}>
              <button 
                className="auth-modal-button"
                onClick={() => { setShowDeleteCategoryModal(false); setPendingDeleteCategory(null); }}
                style={{ 
                  background: 'white', 
                  color: '#2d3748', 
                  border: '1px solid #cbd5e0',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Cancel
              </button>
              <button 
                className="auth-modal-button"
                onClick={confirmDeleteCategory}
                style={{ 
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal (Error/Success) */}
      {showMessageModal && (
        <div className="auth-modal-overlay" onClick={() => setShowMessageModal(false)}>
          <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="auth-modal-title">{messageModalContent.title}</h2>
            <p className="auth-modal-message">{messageModalContent.message}</p>
            <button 
              className="auth-modal-button"
              onClick={() => setShowMessageModal(false)}
              style={{ 
                background: messageModalContent.title === 'Error' 
                  ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default ReceiptScopeTab;
